import { statusObject } from "../utils/allProductStatus";
import { cashnator, cashnatorFloat } from "../utils/functions";
import { ColumnDirective, ColumnsDirective, ExcelFilter, Filter, GridComponent, Inject, Page, Sort, ColumnChooser, ExcelExport, Toolbar } from "@syncfusion/ej2-react-grids";
import { useContext, useEffect, useRef } from "react";
import AuthContext from "../../context/authProvider";
import { getGradingTypeLabel, getPreLacreFormatLabel, normalizeGradingType } from "../utils/gradingTypes";
import { selectIdRange, toggleId } from "./productSelectionRange";

const ProductsList = ({ gradingList, setGradingList, select, products }) => {
    const { user } = useContext(AuthContext);
    const gridRef = useRef(null);
    const anchorIdRef = useRef(null);
    const shiftKeyRef = useRef(false);
    const removeShiftListenerRef = useRef(null);

    useEffect(() => {
        if (!select) {
            anchorIdRef.current = null;
        }
    }, [select]);

    useEffect(
        () => () => {
            removeShiftListenerRef.current?.();
            removeShiftListenerRef.current = null;
        },
        []
    );

    const handleGridCreated = () => {
        removeShiftListenerRef.current?.();
        const element = gridRef.current?.element;
        if (!element) return;
        const onMouseDown = (event) => {
            shiftKeyRef.current = Boolean(event.shiftKey);
        };
        element.addEventListener("mousedown", onMouseDown, true);
        removeShiftListenerRef.current = () =>
            element.removeEventListener("mousedown", onMouseDown, true);
    };

    const getVisibleIds = () => {
        const view = gridRef.current?.getCurrentViewRecords?.() || [];
        return view.map((row) => row?._id).filter(Boolean);
    };

    const applySelectionClick = (id, shiftKey) => {
        if (!id) return;
        if (shiftKey && anchorIdRef.current != null) {
            const visibleIds = getVisibleIds();
            setGradingList((current) =>
                selectIdRange(visibleIds, anchorIdRef.current, id, current)
            );
        } else {
            setGradingList((current) => toggleId(current, id));
        }
        anchorIdRef.current = id;
    };

    const handleRecordClick = (args) => {
        if (!select) return;
        const target = args?.target;
        if (target?.closest?.("a")) return;
        if (target?.closest?.('input[type="checkbox"]')) return;
        const id = args?.rowData?._id;
        const shiftKey = Boolean(args?.event?.shiftKey || shiftKeyRef.current);
        applySelectionClick(id, shiftKey);
    };

    const handleCheckboxClick = (event, id) => {
        event.stopPropagation();
        applySelectionClick(id, event.shiftKey);
    };

    const handleQueryCellInfo = (args) => {
        if (args.column.field === "gradedAt") {
            if (args.data.gradedAt) {
                args.cell.style.color = "green";
            } else {
                args.cell.style.color = "red";
            }
        }
    };

    const filterSettings = { type: "Excel" };

    let toolbarOptions = ["ColumnChooser"];
    let allowExcelExport = false;

    if (user.roles.includes("master")) {
        toolbarOptions = ["ExcelExport", "ColumnChooser"];
        allowExcelExport = true;
    }

    const showCreatedByAdmin = (value) => {
        if (value === true) {
            return "Sim";
        } else if (value === false) {
            return "Não";
        } else {
            return "";
        }
    };

    const toolbarClick = (args) => {
        if (gridRef.current && args.item.id === "Grid_excelexport") {
            // Leva também as colunas ocultas (valor declarado, taxa de manuseio,
            // frete, valor da graduação e as demais opcionais) para a planilha.
            gridRef.current.excelExport({ includeHiddenColumn: true });
        }
    };

    const rowDataBound = (args) => {
        if (select && args?.row) {
            args.row.style.cursor = "pointer";
        }
    };

    return (
        <GridComponent
            id="Grid"
            toolbar={toolbarOptions}
            dataSource={products}
            filterSettings={filterSettings}
            allowExcelExport={allowExcelExport}
            allowFiltering
            allowSorting
            allowPaging
            queryCellInfo={handleQueryCellInfo}
            pageSettings={{ pageSize: 30 }}
            toolbarClick={toolbarClick}
            recordClick={handleRecordClick}
            rowDataBound={rowDataBound}
            created={handleGridCreated}
            ref={(g) => {
                gridRef.current = g;
            }}
            showColumnChooser
        >
            <ColumnsDirective>
                {select ? (
                    <ColumnDirective
                        field="user.name"
                        headerText=""
                        width="100"
                        textAlign="Center"
                        template={(d) => (
                            <input
                                readOnly
                                checked={gradingList.includes(d?._id)}
                                type="checkbox"
                                onClick={(event) => handleCheckboxClick(event, d?._id)}
                            />
                        )}
                    />
                ) : null}
                <ColumnDirective field="user.name" headerText="Nome" width="100" textAlign="Center" />
                <ColumnDirective
                    field="gradingType"
                    headerText="Tipo"
                    width="120"
                    textAlign="Center"
                    valueAccessor={(f, d) => getGradingTypeLabel(d?.gradingType)}
                />
                <ColumnDirective
                    field="autographEvaluation"
                    headerText="Autógrafo"
                    width="110"
                    textAlign="Center"
                    valueAccessor={(f, d) => (d?.autographEvaluation ? "Sim" : "Não")}
                />
                <ColumnDirective
                    field="preLacreFormat"
                    headerText="Pré lacre"
                    visible={false}
                    width="150"
                    textAlign="Center"
                    valueAccessor={(f, d) =>
                        normalizeGradingType(d?.gradingType) === "pre" && d?.preLacreFormat
                            ? getPreLacreFormatLabel(d?.preLacreFormat)
                            : "-"
                    }
                />
                <ColumnDirective field="numberOfCards" headerText="Nº de itens" width="100" textAlign="Center" />
                <ColumnDirective field="delivery" width="100" headerText="Entrega" textAlign="Center" />
                <ColumnDirective
                    field="finalPrice"
                    width="100"
                    headerText="Valor final"
                    textAlign="Left"
                    valueAccessor={(f, d) => cashnator(d?.finalPrice)}
                />
                <ColumnDirective
                    field="cardsValue"
                    width="120"
                    headerText="Valor declarado"
                    visible={false}
                    textAlign="Left"
                    valueAccessor={(f, d) => cashnator(d?.cardsValue)}
                />
                <ColumnDirective
                    field="insurance"
                    width="120"
                    headerText="Taxa de manuseio"
                    visible={false}
                    textAlign="Left"
                    valueAccessor={(f, d) => cashnator(d?.insurance)}
                />
                <ColumnDirective
                    field="shipping"
                    width="120"
                    headerText="Frete pago"
                    visible={false}
                    textAlign="Left"
                    valueAccessor={(f, d) => cashnatorFloat(d?.shipping)}
                />
                <ColumnDirective
                    field="gradingValue"
                    width="120"
                    headerText="Valor graduação"
                    visible={false}
                    textAlign="Left"
                    valueAccessor={(f, d) => cashnator(d?.gradingValue)}
                />
                <ColumnDirective
                    field="createdAt"
                    width="100"
                    headerText="Criado"
                    textAlign="Center"
                    type="date"
                    format="dd/MM/yyyy"
                />
                <ColumnDirective
                    field="receivedAt"
                    width="100"
                    headerText="Recebido"
                    textAlign="Center"
                    type="date"
                    format="dd/MM/yyyy"
                />
                <ColumnDirective
                    field="paidAt"
                    width="100"
                    headerText="Pago"
                    visible={false}
                    textAlign="Center"
                    format="dd/MM/yyyy"
                    type="date"
                />
                <ColumnDirective
                    field="calculatedDeadline"
                    headerText="Prazo"
                    width="100"
                    textAlign="Center"
                    format="dd/MM/yyyy"
                    type="date"
                />
                <ColumnDirective
                    field="status"
                    width="100"
                    headerText="Status"
                    textAlign="Center"
                    valueAccessor={(f, d) => statusObject[d?.status]}
                />
                <ColumnDirective field="trackingCode" headerText="Rastreio" width="100" textAlign="Center" type="string" />
                <ColumnDirective
                    field="coupon.code"
                    headerText="Cupom"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                />
                <ColumnDirective
                    field="postalCode"
                    headerText="CEP"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                />
                <ColumnDirective
                    field="city"
                    headerText="Cidade"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                />
                <ColumnDirective
                    field="state"
                    headerText="Estado"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                />
                <ColumnDirective
                    field="store.name"
                    headerText="Loja"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                />
                <ColumnDirective
                    field="gradedAt"
                    width="100"
                    headerText="Em Processo de Lacre"
                    textAlign="Center"
                    valueAccessor={(f, d) => (d?.gradedAt ? "✔" : "X")}
                />
                <ColumnDirective
                    field="createdByAdmin"
                    headerText="Criado por Admin?"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                    valueAccessor={(f, d) => showCreatedByAdmin(d?.createdByAdmin)}
                />
                <ColumnDirective
                    field="paymentMethod"
                    headerText="Forma de Pagamento"
                    visible={false}
                    width="100"
                    textAlign="Center"
                    type="string"
                    valueAccessor={(f, d) =>
                        d?.paymentMethod === "PIX"
                            ? "PIX"
                            : d?.paymentMethod === "CREDIT_CARD"
                              ? "Cartão de Crédito"
                              : ""
                    }
                />
                <ColumnDirective
                    width="70"
                    textAlign="Center"
                    template={(d) => (
                        <a href={`/manafix/products/${d?._id}`} className="bg-blue-500 hover:bg-blue-800 px-2 text-black">
                            Ver
                        </a>
                    )}
                />
            </ColumnsDirective>
            <Inject services={[Sort, Page, Filter, ExcelFilter, ColumnChooser, ExcelExport, Toolbar]} />
        </GridComponent>
    );
};

export default ProductsList;
